import { useCallback, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import getConfig from 'next/config';
import Router from 'next/router';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { ROUTES } from '_constants/routes';

import { refreshSearch, refreshPage } from '_redux/search/slice';
import { selectSearch } from '_redux/search/selector';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import FAQSection from '_components/composed/sections/FAQSection';
import HomeBannerSection from '_components/composed/sections/HomeBannerSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import ThreeStepSection from '_components/composed/sections/ThreeStepSection';
import ProtectBusinessSection from '_components/composed/sections/ProtectBusinessSection';
import TrustPilotSection from '_components/composed/sections/TrustPilotSection';

const { devices } = theme;
const { publicRuntimeConfig } = getConfig();

const StyledHomePage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledCircle = styled(forwardRef(({ size, rotate, top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;

  background: ${`linear-gradient(180deg, ${theme.colors.blue[500]} 0%, rgba(46, 144, 250, 0) 100%)`};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  ${({ rotate }) =>
    rotate &&
    css`
      transform: ${`rotate(${rotate}deg)`};
    `}

  ${({ size }) =>
    size &&
    css`
      width: ${size}px;
      height: ${size}px;
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledImageContainer = styled(forwardRef(({ top, bottom, left, right, ...props }, ref) => (<div ref={ref} {...props} />)))`
  position: absolute;
  z-index: -1;

  ${({ top }) =>
    typeof top !== 'undefined' &&
    css`
      top: ${typeof top === 'string' ? top : `${top}px`};
    `}

  ${({ bottom }) =>
    typeof bottom !== 'undefined' &&
    css`
      bottom: ${typeof bottom === 'string' ? bottom : `${bottom}px`};
    `}

  ${({ left }) =>
    typeof left !== 'undefined' &&
    css`
      left: ${typeof left === 'string' ? left : `${left}px`};
    `}

  ${({ right }) =>
    typeof right !== 'undefined' &&
    css`
      right: ${typeof right === 'string' ? right : `${right}px`};
    `}

  @media ${({ theme }) => theme.devices.tablet} {
    display: none;
  }
`;

const HomePage = () => {
  const dispatch = useDispatch();
  const search = useSelector(selectSearch);

  const handleChangeSearch = useCallback(
    (search) => {
      dispatch(refreshSearch(search));
    },
    [dispatch]
  );

  const handleChangePage = useCallback(
    (page) => {
      dispatch(refreshPage(page));
    },
    [dispatch]
  );

  return (
    <StyledHomePage>
      <HomeBannerSection
        search={search}
        setSearch={handleChangeSearch}
        setPage={handleChangePage}
      />
      <ThreeStepSection
        title="3 Step Process"
        subTitle="Get your registered trademark in 3 easy steps."
        steps={[
          {
            header: 'Search for Availability',
            description:
              'Check to see if your trademark is already being used.',
            icon: `${publicRuntimeConfig.basePath}/images/file-searching.svg`,
          },
          {
            header: 'Answer a few Questions',
            description:
              'Fill out our simple questionnaire to begin the registration process.',
            icon: `${publicRuntimeConfig.basePath}/images/my-answers-home.svg`,
          },
          {
            header: 'Application Filing',
            description:
              'Your application will be filed with the U.S. Patent and Trademark Office (USPTO)',
            icon: `${publicRuntimeConfig.basePath}/images/app-development-home.svg`,
          },
        ]}
      />
      <TrustPilotSection
        title="Customer Reviews & Testimonials"
        subTitle="See why others are choosing Trademark Genius!"
      />
      <BottomBannerSection
        header={
          <>
            Ensure that competitors don&#39;t infringe on your most vital
            assets.
          </>
        }
        footer={
          <>
            100% Satisfaction Guaranteed.{' '}
            <Button
              suffix={
                <Icon
                  alias={ICONS_ALIASES.arrowRight}
                  color={theme.colors.blue[500]}
                />
              }
              color={theme.colors.white}
              onClick={() => Router.push(ROUTES.trademarkRegistration)}
              textColor={theme.colors.blue[500]}
              hoverColor={theme.colors.white}
              hoverTextColor={theme.colors.blue[500]}
            >
              Start Registering Now
            </Button>
          </>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/shield.svg`}
        imgTopDesktopPosition="-100px"
      />
      <ProtectBusinessSection />
      <FAQSection />
      <BottomBannerSection
        header={<>Don&#39;t wait, Protect Your Brand now</>}
        footer={
          <Button
            suffix={
              <Icon
                alias={ICONS_ALIASES.arrowRight}
                color={theme.colors.blue[500]}
              />
            }
            color={theme.colors.white}
            onClick={() => Router.push(ROUTES.trademarkRegistration)}
            textColor={theme.colors.blue[500]}
            hoverColor={theme.colors.white}
            hoverTextColor={theme.colors.blue[500]}
          >
            Start Registering Now
          </Button>
        }
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademark-registration-bottom.svg`}
        imgTopDesktopPosition="-130px"
        moreSpace
        disclaimer
      />
      <StyledCircle size={686} rotate={90} left={-273} top={'43%'} />
      <StyledImageContainer left={80} top={'55%'}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
      <StyledCircle size={752} rotate={-180} right={-192} top={'67%'} />
      <StyledCircle size={876} rotate={90} left={-230} bottom={150} />
      <StyledImageContainer left={80} bottom={325}>
        <picture>
          <source
            srcSet={`${publicRuntimeConfig.basePath}/images/dots.svg`}
            type="image/svg"
          />
          <img src={`${publicRuntimeConfig.basePath}/images/dots.svg`} alt="" />
        </picture>
      </StyledImageContainer>
    </StyledHomePage>
  );
};

export default HomePage;
